import { type DependencyList, useEffect, useRef, useState } from 'react';
import { useEventCallback } from './useEventCallback';

export interface UseApiDataOptions<T> {
	refreshOnFocus?: boolean;
	initialData?: T;

	/**
	 * use it to make the api call lazy if certain conditions are not met
	 * @default true
	 */
	enabled?: boolean;
	/**
	 * Throttle the focus refresh to avoid multiple requests in a short period of time
	 * @default 5000 ms
	 */
	focusThrottleInterval?: number;
}

export interface UseApiDataReturn<T> {
	data: T | null;
	status: 'loading' | 'refreshing' | 'idle';
	error: Error | null;
}

interface FetcherOptions {
	fetchReason: 'mount' | 'focus' | 'force';
	signal: AbortSignal;
}

export function useApiData<T>(
	fetcher: (options: FetcherOptions) => Promise<T>,
	dependencies: DependencyList = [],
	options: UseApiDataOptions<T> = {},
): [UseApiDataReturn<T>, () => Promise<void>] {
	const { enabled = true } = options;

	const [data, setData] = useState<T | null>(options.initialData ?? null);
	const [status, setStatus] = useState<'loading' | 'refreshing' | 'idle'>(
		enabled ? 'loading' : 'idle',
	);
	const [error, setError] = useState<Error | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const lastFocusRefreshRef = useRef<number>(0);
	const previousStatusRef = useRef(status); // Track previous status

	const liveFetcherRef = useEventCallback(fetcher);

	// Cleanup function to abort any pending request
	const cleanup = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
	};

	const fetchData = async (fetchReason: FetcherOptions['fetchReason']) => {
		// For focus events, check if we should throttle
		if (fetchReason === 'focus') {
			const now = Date.now();
			const timeSinceLastRefresh = now - lastFocusRefreshRef.current;
			const throttleInterval = options.focusThrottleInterval ?? 5000;

			if (timeSinceLastRefresh < throttleInterval) {
				return;
			}
			lastFocusRefreshRef.current = now;
		}

		// Cancel any ongoing request
		cleanup();

		// Create new abort controller for this request
		abortControllerRef.current = new AbortController();
		const signal = abortControllerRef.current.signal;

		try {
			// Set status based on previous status for focus refreshes
			const prevStatus = previousStatusRef.current;
			setStatus(
				fetchReason === 'focus' && prevStatus === 'idle'
					? 'refreshing'
					: 'loading',
			);
			// Wrap fetcher in a promise that can be aborted
			const fetchPromise = liveFetcherRef({ fetchReason, signal });
			const result = await Promise.race([
				fetchPromise,
				new Promise<never>((_, reject) => {
					signal.addEventListener('abort', () => {
						reject(new Error('ABORTED'));
					});
				}),
			]);

			// Only update state if the request wasn't aborted
			if (!signal.aborted) {
				setData(result);
				setError(null);
				setStatus('idle');
			}
		} catch (e) {
			// Only update error state if the request wasn't aborted
			if (!signal.aborted && !(e instanceof Error && e.message === 'ABORTED')) {
				setError(e as Error);
				setStatus('idle');
			}
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <cleanup is not a dependency>
	useEffect(() => {
		if (!enabled) return;
		fetchData('mount');
		return cleanup;
	}, [...dependencies, enabled]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <cleanup is not a dependency>
	useEffect(() => {
		if (!options.refreshOnFocus) return;

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				fetchData('focus');
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			cleanup();
		};
	}, [options.refreshOnFocus, options.focusThrottleInterval, ...dependencies]);

	useEffect(() => {
		previousStatusRef.current = status; // Update previous status ref on status change
	});

	return [{ data, status, error }, () => fetchData('force')];
}
